import { isString } from 'lodash';
import { useState, ReactNode, useEffect } from 'react';
import { useDropzone, DropzoneOptions } from 'react-dropzone';

import { alpha, Theme, styled } from '@mui/material/styles';
import { Box, Paper, SxProps, Typography } from '@mui/material';

import useLocales from 'src/hooks/useLocales';

import { CustomFile } from 'src/@types/fileView/types';

import { ProfileUpdate } from 'src/sections/profile/ProfileEditForm';

import Image from '../Image';
import Iconify from '../Iconify';
import { fData } from '../../utils/formatNumber';

// ----------------------------------------------------------------------

const accept = '.png, .jpg';

const RootStyle = styled('div')(({ theme }) => ({
  width: 144,
  height: 144,
  margin: 'auto',
  borderRadius: '50%',
  padding: theme.spacing(1),
  border: `1px dashed ${theme.palette.grey[500_32]}`,
}));

const DropZoneStyle = styled('div')({
  zIndex: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  display: 'flex',
  overflow: 'hidden',
  borderRadius: '50%',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  '& > *': { width: '100%', height: '100%' },
  '&:hover': {
    cursor: 'pointer',
    '& .placeholder': { zIndex: 9 },
  },
});

const PlaceholderStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.neutral,
  transition: theme.transitions.create('opacity', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
}));

interface Props extends DropzoneOptions {
  error?: boolean;
  file: CustomFile | string | null;
  caption?: ReactNode;
  sx?: SxProps<Theme>;
  submitTrigger: number;
  handleSubmit: (values: CustomFile | null | string) => void;
  profile?: ProfileUpdate;
  setProfile?: (value: ProfileUpdate) => void;
}

export default function UploadAvatar({
  error,
  file,
  caption,
  sx,
  submitTrigger,
  handleSubmit,
  profile,
  setProfile,
  ...other
}: Props) {
  const { t } = useLocales();
  const [imgFile, setImgFile] = useState<any>(null);
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: false,
    accept,
    ...other,
    onDropAccepted: (files) => {
      const f: CustomFile = files[0];
      f.preview = URL.createObjectURL(files[0]);
      setImgFile(f);
      profile && setProfile?.({ profile: profile.profile, image: f });
    },
  });

  useEffect(() => {
    setImgFile(file);
  }, [file]);

  useEffect(() => {
    if (submitTrigger) {
      handleSubmit(imgFile ?? '');
    }
  }, [submitTrigger]);

  const deleteImage = () => {
    setImgFile(null);
    profile && setProfile?.({ profile: profile?.profile, image: null });
  };

  // Merge DropZone styles
  // Merge DropZone styles
  // Styles for DropZone interaction states
  const dropZoneSx: SxProps<Theme> = (theme) => ({
    ...(isDragActive ? { opacity: 0.72 } : {}),
    ...(isDragReject || error
      ? {
          color: theme.palette.error.main,
          borderColor: theme.palette.error.light,
          bgcolor: theme.palette.error.lighter,
        }
      : {}),
  });

  // Merge Placeholder styles
  const placeholderSx: SxProps<Theme> = (theme) => ({
    ...(imgFile && {
      opacity: 0,
      color: theme.palette.common.white,
      bgcolor: theme.palette.grey[900],
      '&:hover': { opacity: 0.72 },
    }),
  });

  return (
    <>
      <RootStyle sx={sx}>
        {imgFile && (
          <Iconify
            onClick={deleteImage}
            icon="ic:round-close"
            sx={{
              width: 24,
              height: 24,
              mb: 1,
              ml: '125px',
              position: 'absolute',
              '&:hover': { opacity: 0.72 },
            }}
          />
        )}
        <DropZoneStyle {...getRootProps()} sx={dropZoneSx} id="3MK3fy">
          <input {...getInputProps()} />
          {imgFile && (
            <Image
              alt="avatar"
              src={isString(imgFile) ? imgFile : imgFile.preview}
              sx={{ zIndex: 8 }}
            />
          )}
          <PlaceholderStyle className="placeholder" sx={placeholderSx}>
            <Iconify icon="ic:round-add-a-photo" sx={{ width: 24, height: 24, mb: 1 }} />
            <Typography variant="caption">
              {imgFile ? t('profile.updateAvatar') : t('profile.uploadAvatar')}
            </Typography>
          </PlaceholderStyle>
        </DropZoneStyle>
      </RootStyle>
      {caption}
      {fileRejections.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            py: 1,
            px: 2,
            my: 2,
            borderColor: 'error.light',
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
          }}
        >
          {fileRejections.map(({ file: f, errors }) => (
            <Box key={f.name} sx={{ my: 1 }}>
              <Typography variant="subtitle2" noWrap>
                {f.name} - {fData(f.size)}
              </Typography>
              {errors.map((e) => (
                <Typography key={e.code} variant="caption" component="p">
                  - {e.message}
                </Typography>
              ))}
            </Box>
          ))}
        </Paper>
      )}
    </>
  );
}
